import { useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Local/e2e escape hatch ONLY: when REACT_APP_AUTH_DISABLED === "true" the guard
// is bypassed so the protected UI can be rendered without an Entra session.
const AUTH_DISABLED = process.env.REACT_APP_AUTH_DISABLED === "true";

// Runtime-injected via window.ENV (docker-entrypoint.sh). Comma-separated list of allowed domains.
const ALLOWED_DOMAINS: string[] = (
  (window as any).ENV?.ALLOWED_EMAIL_DOMAIN ||
  process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN ||
  "dag.com"
)
  .toLowerCase()
  .split(",")
  .map((d: string) => d.trim())
  .filter(Boolean);

const returnTo = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const Spinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
  </div>
);

function getAccountEmail(account: any): string | undefined {
  return (
    account?.username ||
    account?.idTokenClaims?.preferred_username ||
    account?.idTokenClaims?.email ||
    account?.idTokenClaims?.upn
  );
}

const emailAllowed = (email?: string) =>
  !!email && ALLOWED_DOMAINS.some((d) => email.toLowerCase().endsWith(`@${d}`));

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const loginStarted = useRef(false);

  const activeAccount = instance.getActiveAccount();
  const email = getAccountEmail(activeAccount);

  useEffect(() => {
    if (AUTH_DISABLED || isAuthenticated || loginStarted.current || inProgress !== InteractionStatus.None) {
      return;
    }
    loginStarted.current = true;
    instance.loginRedirect({
      scopes: ["openid", "profile", "email", "offline_access"],
      redirectStartPage: returnTo(),
    });
  }, [isAuthenticated, inProgress, instance]);

  if (AUTH_DISABLED) {
    return <>{children}</>;
  }

  if (inProgress !== InteractionStatus.None || !isAuthenticated) {
    return <Spinner />;
  }

  if (!emailAllowed(email)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#0d0d0a] px-4">
        <div className="max-w-md w-full bg-[#1a1917] border border-[#262420] p-8 text-center">
          <h1 className="text-xl font-semibold text-[#E5E0D5]">Access restricted</h1>
          <p className="mt-3 text-sm text-[#9D9585]">
            This system is limited to{" "}
            <span className="font-mono">{ALLOWED_DOMAINS.map((d) => `@${d}`).join(", ")}</span> Microsoft Entra accounts.
          </p>
          {email && (
            <p className="mt-2 text-xs text-[#5A5448]">Signed in as {email}.</p>
          )}
          <button
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
            className="mt-6 bg-[#C9A260] hover:bg-[#B8913F] text-[#111110] px-5 py-2 text-sm font-medium"
          >
            Sign out &amp; switch account
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
